import Vue from 'vue'
import gql from 'graphql-tag'
import { State } from 'vuex-class'
import { mixins } from 'vue-class-component'
import { Component, Watch } from 'vue-property-decorator'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Case } from '@/modules/Case/interfaces/case'
import { ApolloResponse } from '@/interfaces/apollo'
import { showErrors } from '@/utils/snack'
import { translateCaseTypeKey } from '@/utils/translate'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppTableDate from '@/components/AppTableDate.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppTextSearch from '@/components/AppTextSearch.vue'
import AppListSearchBtn from '@/components/AppListSearchBtn.vue'
import { getCases, getAllGlobalSearchCases } from '@/modules/Case/gql/case.gql'
import { caseType, caseTypeClass, casePriority, casePriorityClass, casePriorityValues, caseTypeValues, casePriorityKeys, caseTypeKeys } from '@/modules/Case/helpers/case'
import SearchUser from '@/mixins/search-queries/search-user'
import RecordTypesHandler from '@/mixins/record-types'

import CaseDialog from '@/modules/Case/scripts/case-dialog'
import ListPage from '@/mixins/list-page'
import AppFilterButton from '@/components/AppFilterButton.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import AppSimpleTextSearch from '@/components/AppSimpleTextSearch.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'
import { PageState } from '../../../store/modules/page'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppRefresh,
    AppTextField,
    AppTableDate,
    AppSelector,
    AppListSearchBtn,
    AppTextSearch,
    AppFilterButton,
    AppSimpleTextField,
    AppSimpleTextSearch,
    AppSimpleSelector
  }
})
export default class CaseList extends mixins(RecordTypesHandler, CaseDialog, ListPage, SearchUser) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Insurance Cases'
    },
    {
      text: 'Case',
      to: { name: 'CaseDashboard' }
    }
  ]

  allCases: Case[] = []

  caseTypeClass = caseTypeClass

  caseType = caseType

  casePriority = casePriority

  casePriorityClass = casePriorityClass

  casePriorityOptions = casePriorityValues

  caseTypeOptions = caseTypeValues

  get recordTypeOptions () {
    if (this.$R.isEmpty(this.recordTypesOptions)) {
      return []
    }
    return this.recordTypesOptions['POLICY'].map(template => template.title)
  }

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    createdAt: 'CREATED_AT',
    caseName: 'CASE_NAME',
    caseType: 'CASE_TYPE',
    priority: 'CASE_PRIORITY',
    updatedAt: 'UPDATED_AT'
  }

  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  translateCaseTypeKey = translateCaseTypeKey

  tableColumn = [
    { field: 'id',
      title: 'Case ID',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.id ? row.id : '-')
        ]
      }
      },
      sortable: true,
      resizable: true,
      width: 120,
      fixed: 'left'
    },
    { field: 'inquiryName',
      title: 'Case Name',
      slots: { default: 'name_default' },
      resizable: true,
      sortable: true
    },
    { field: 'priority',
      title: 'Priority',
      slots: { default: 'priority_default' },
      resizable: true,
      sortable: true,
      width: 150
    },
    { title: 'Account Name',
      field: 'account',
      slots: { default: 'account_name_default' },
      resizable: true
    },
    {
      field: 'type',
      title: 'Case Type',
      slots: { default: 'type_default' },
      sortable: true,
      resizable: true,
      width: 150
    },
    { title: 'recordType',
      field: 'Record Type',
      slots: { default: 'record_type_default' },
      resizable: true,
      width: 180
    },
    { title: 'Last Modified Time',
      field: 'updatedAt',
      slots: {
        default: 'update_time_default'
      },
      resizable: true,
      sortable: true,
      width: 120
    },
    {
      title: 'Case Manager',
      field: 'caseManager',
      slots: {
        default: 'case_manager_default'
      },
      resizable: true,
      sortable: false
    },
    {
      title: 'Verge Staffs',
      field: 'assignedUsers',
      slots: {
        default: 'staff_default'
      },
      resizable: true,
      sortable: false
    },
    { field: 'status', title: 'Status', slots: { default: 'action_default' }, sortable: false, width: 80, resizable: true },
    { field: 'closed', title: 'Open Days', slots: { default: 'open_date_default' }, fixed: 'right', width: 120 }
  ]

  tableLoading = false

  get isShowFilter () {
    return this.$route.name !== 'CaseListWithSearchval'
  }

  @State(state => state.page) page!: PageState

  async created () {
    const { type, search } = this.$route.params
    const breadcrumbText = type === 'openning' ? 'Openning' : ''
    this.breadcrumbs.push({
      text: `All ${breadcrumbText} Cases`
    })
    this.restorePage(this.page[this.$route.name || ''])
    await this.getRecordTypes('POLICY')
  }

  async mounted () {
    this.onResize()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getList()
  }

  getList () {
    if (this.$route.name === 'CaseListWithSearchval') {
      this.getCasesFromDashboard()
    } else {
      this.getCases()
    }
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  routeToDetail (item: any) {
    return {
      name: 'CaseDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  routeToAccountDetail (item: any) {
    if (item.account) {
      return {
        name: 'AccountDetailDetail',
        params: {
          id: item.account.id
        }
      }
    }
  }

  routeToContactDetail (item: any) {
    if (item.primaryContact) {
      return {
        name: 'ContactDetailDetail',
        params: {
          id: item.primaryContact.id
        }
      }
    }
  }

  getCases () {
    const { type } = this.$route.params
    const filter: {
      [key: string]: any;
    } = {}
    if (this.filter.caseName) {
      filter.inquiryName = this.filter.caseName
    }
    if (this.filter.pripority !== null && !isNaN(this.filter.pripority) && this.filter.pripority !== -1) {
      filter.priority = Number(casePriorityKeys[this.filter.pripority])
    }
    if (this.filter.accountName) {
      filter.accountName = this.filter.accountName
    }
    if (this.filter.caseType !== null && !isNaN(this.filter.caseType) && this.filter.caseType !== -1) {
      filter.type = caseTypeKeys[this.filter.caseType]
    }
    if (this.filter.recordType !== null && !isNaN(this.filter.recordType) && this.filter.recordType !== -1) {
      filter.recordTypeId = Number(this.recordTypes[this.filter.recordType].id)
    }
    if (this.filter.caseManager && this.filter.caseManager.id !== undefined) {
      filter.caseManagerId = Number(this.filter.caseManager.id)
    }
    if (type === 'openning') {
      this.getTopCases(filter)
    } else {
      this.getAllCases(filter)
    }
    this.storePage()
  }

  async getTopCases (filter: {[key: string]: any}) {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCases,
        variables: {
          filter,
          isTop: true,
          isOpen: true,
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.allCases = result.data.getCases.cases
      this.tableLoading = false
      this.options.tablePage.total = result.data.getCases.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getAllCases (filter: {[key: string]: any}) {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCases,
        variables: {
          filter,
          isTop: false,
          isOpen: false,
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.allCases = result.data.getCases.cases
      this.tableLoading = false
      this.options.tablePage.total = result.data.getCases.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getCasesFromDashboard () {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getAllGlobalSearchCases,
        variables: {
          query: this.$route.params.search,
          isTop: true,
          isOpen: true,
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.allCases = result.data.globalSearchCases.cases
      this.tableLoading = false
      this.options.tablePage.total = result.data.globalSearchCases.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  sortTable (column: any) {
    this.options.sortBy = column.property
    this.options.sortDesc = column.order === 'desc'
    this.getList()
  }

  handlePageChange (pageData: any) {
    this.options.tablePage.currentPage = pageData.currentPage
    this.options.tablePage.pageSize = pageData.pageSize
    this.getList()
  }

  openedDays (caseInfo: Case) {
    const { openDate, closeDate, closed } = caseInfo

    const closeDateM = this.$moment.unix(closeDate).tz('America/New_York')
    const openDateM = this.$moment.unix(openDate).tz('America/New_York')
    const nowM = this.$moment().tz('America/New_York')

    return closed
      ? closeDateM.diff(openDateM, 'days')
      : nowM.diff(openDateM, 'days')
  }

  async refreshList () {
    await this.clearPage()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getList()
  }
}
